import React, { useEffect, useState } from "react";
import { api, streamSSE } from "@/lib/api";
import Markdown from "@/components/Markdown";
import { toast } from "sonner";
import {
  Layers, Plus, Trash2, Loader2, Save, X, Download, FileCheck2, ArrowRight,
} from "lucide-react";

const STATUS_COLORS = {
  open:      "border-slate-300 bg-slate-100 text-slate-700",
  verified:  "border-sky-300 bg-sky-100 text-sky-700",
  validated: "border-emerald-300 bg-emerald-100 text-emerald-700",
  closed:    "border-slate-400 bg-slate-200 text-slate-800",
};

export default function DesignControlsPanel({ projectId, project }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [dhfContent, setDhfContent] = useState("");
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/design-controls/items`, { params: { project_id: projectId } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load design controls");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const create = async (draft) => {
    try {
      await api.post(`/design-controls/items`, { ...draft, project_id: projectId });
      toast.success("Row added");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add");
    }
  };

  const update = async (id, patch) => {
    try {
      await api.put(`/design-controls/items/${id}`, patch);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this design-control row?")) return;
    try {
      await api.delete(`/design-controls/items/${id}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const generateDhf = async () => {
    if (items.length === 0) {
      toast.error("Add at least one row before generating a DHF section.");
      return;
    }
    setGenerating(true);
    setDhfContent("");
    let buf = "";
    try {
      await streamSSE(
        "/design-controls/generate-dhf-section",
        {
          project_id: projectId,
          device_name: project?.device_name || project?.name || "Device",
          intended_use: project?.description || "",
        },
        (frame) => {
          if (frame.delta) {
            buf += frame.delta;
            setDhfContent(buf);
          }
          if (frame.error) toast.error(frame.error);
        }
      );
    } catch (e) {
      toast.error(e.message || "DHF generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const downloadDhf = () => {
    const blob = new Blob([dhfContent], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(project?.device_name || "device").replace(/\s+/g, "_")}_dhf_section.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportCsv = () => {
    const rows = [
      ["id","user_need","design_input","design_output","verification","validation","acceptance_criteria","cfr_ref","status","owner"],
      ...items.map((it) => [
        it.id, it.user_need, it.design_input, it.design_output, it.verification, it.validation,
        it.acceptance_criteria, it.cfr_ref, it.status, it.owner,
      ].map((v) => `"${String(v ?? "").replace(/"/g,'""')}"`).join(",")),
    ];
    const csv = rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `design_controls_${projectId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!projectId) {
    return (
      <div className="flex h-64 flex-col items-center justify-center border border-dashed border-slate-200 bg-white text-center text-slate-500">
        <Layers className="mb-3 h-6 w-6 text-[#2563EB]" strokeWidth={1.5} />
        <div className="text-sm">Pick a project first.</div>
        <div className="mt-1 text-xs text-slate-400">Design controls live inside a project.</div>
      </div>
    );
  }

  return (
    <div data-testid="dc-root" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline mb-1 text-[#2563EB]">DESIGN CONTROLS · 21 CFR 820.30 · ISO 13485 §7.3</div>
          <h2 className="font-serif text-2xl font-semibold text-slate-900">
            {project?.device_name || "Design controls"}
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            User needs → design inputs → outputs → V&amp;V evidence. {items.length} row{items.length === 1 ? "" : "s"}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="dc-add-btn" onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 bg-[#2563EB] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1D4ED8]">
            <Plus className="h-3.5 w-3.5" /> Add row
          </button>
          <button data-testid="dc-generate-dhf-btn" onClick={generateDhf} disabled={generating || items.length === 0} className="inline-flex items-center gap-1.5 border border-[#2563EB]/50 bg-[#2563EB]/5 px-3 py-1.5 text-xs font-semibold text-[#1D4ED8] hover:bg-[#2563EB]/10 disabled:opacity-40">
            {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileCheck2 className="h-3.5 w-3.5" />}
            Generate DHF section
          </button>
          <button onClick={exportCsv} disabled={items.length === 0} className="inline-flex items-center gap-1.5 border border-slate-300 px-3 py-1.5 text-xs text-slate-700 hover:border-slate-400 disabled:opacity-30">
            <Download className="h-3.5 w-3.5" /> CSV
          </button>
        </div>
      </div>

      {/* Traceability Table */}
      <div data-testid="dc-table" className="border border-slate-200 bg-white">
        <div className="hidden border-b border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-slate-500 md:grid md:grid-cols-12 md:gap-3">
          <div className="md:col-span-3">User need</div>
          <div className="md:col-span-2">Design input</div>
          <div className="md:col-span-2">Design output</div>
          <div className="md:col-span-2">Verification</div>
          <div className="md:col-span-2">Validation</div>
          <div className="md:col-span-1 text-right">Status</div>
        </div>
        {items.length === 0 && !loading && (
          <div className="p-8 text-center text-sm text-slate-500">
            No design controls yet. Click <b className="text-slate-900">Add row</b> to start the traceability chain.
          </div>
        )}
        <div className="divide-y divide-slate-100">
          {items.map((it) => (
            <button
              key={it.id}
              data-testid={`dc-row-${it.id}`}
              onClick={() => setEditing(it)}
              className="grid w-full grid-cols-1 items-start gap-3 px-4 py-3 text-left text-xs hover:bg-slate-50 md:grid-cols-12"
            >
              <div className="md:col-span-3">
                <div className="font-medium text-slate-900">{it.user_need || "—"}</div>
                <div className="mt-0.5 font-mono text-[10px] text-slate-400">{it.cfr_ref} · {it.iso_ref}</div>
              </div>
              <div className="md:col-span-2 text-slate-700">
                <FlowMark>{it.design_input}</FlowMark>
              </div>
              <div className="md:col-span-2 text-slate-700">
                <FlowMark>{it.design_output}</FlowMark>
              </div>
              <div className="md:col-span-2 text-slate-700">
                <FlowMark>{it.verification}</FlowMark>
              </div>
              <div className="md:col-span-2 text-slate-700">
                <FlowMark>{it.validation}</FlowMark>
              </div>
              <div className="flex items-center justify-end gap-2 md:col-span-1">
                <span className={`border px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_COLORS[it.status] || STATUS_COLORS.open}`}>
                  {it.status || "open"}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); remove(it.id); }}
                  data-testid={`dc-delete-${it.id}`}
                  className="p-1 text-slate-400 hover:text-rose-500"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </button>
          ))}
        </div>
      </div>

      {dhfContent && (
        <div data-testid="dc-dhf-output" className="border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-3">
            <div>
              <div className="overline text-[#2563EB]">GENERATED DHF SECTION</div>
              <div className="mt-1 text-xs text-slate-500">Grounded strictly in the {items.length} rows above · audit-ready per 21 CFR 820.30</div>
            </div>
            <button onClick={downloadDhf} className="inline-flex items-center gap-1.5 border border-slate-300 px-3 py-1.5 text-xs text-slate-700 hover:border-slate-400">
              <Download className="h-3.5 w-3.5" /> Markdown
            </button>
          </div>
          <div className="max-h-[70vh] overflow-y-auto p-6">
            <Markdown>{dhfContent}</Markdown>
          </div>
        </div>
      )}

      {(editing || creating) && (
        <DcEditor
          item={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSave={async (patch) => {
            if (editing) await update(editing.id, patch);
            else await create(patch);
            setEditing(null); setCreating(false);
          }}
        />
      )}
    </div>
  );
}

function FlowMark({ children }) {
  if (!children) return <span className="text-slate-300">—</span>;
  return <div className="line-clamp-3">{children}</div>;
}

function DcEditor({ item, onClose, onSave }) {
  const [draft, setDraft] = useState(
    item
      ? { ...item }
      : { user_need: "", design_input: "", design_output: "", verification: "", validation: "", acceptance_criteria: "", cfr_ref: "820.30", iso_ref: "ISO 13485 §7.3", status: "open", owner: "" }
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div className="font-serif text-lg font-semibold text-slate-900">
            {item ? "Edit design control row" : "Add design control row"}
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="h-5 w-5" /></button>
        </div>
        <div className="grid gap-3 p-6 md:grid-cols-2">
          <Row label="User need" full>
            <textarea data-testid="dc-input-user-need" value={draft.user_need} onChange={(e) => setDraft({ ...draft, user_need: e.target.value })} rows={2} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
          </Row>
          <Row label="Design input" full>
            <textarea value={draft.design_input} onChange={(e) => setDraft({ ...draft, design_input: e.target.value })} rows={2} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
          </Row>
          <Row label="Design output" full>
            <textarea value={draft.design_output} onChange={(e) => setDraft({ ...draft, design_output: e.target.value })} rows={2} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
          </Row>
          <Row label="Verification (V&V test / evidence)" full>
            <textarea value={draft.verification} onChange={(e) => setDraft({ ...draft, verification: e.target.value })} rows={2} placeholder="e.g. Test report TR-042, IEC 62366-1 usability summary" className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
          </Row>
          <Row label="Validation (clinical / user)" full>
            <textarea value={draft.validation} onChange={(e) => setDraft({ ...draft, validation: e.target.value })} rows={2} placeholder="e.g. Clinical study CS-01, HFE-005 summative" className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
          </Row>
          <Row label="Acceptance criteria" full>
            <input value={draft.acceptance_criteria} onChange={(e) => setDraft({ ...draft, acceptance_criteria: e.target.value })} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
          </Row>
          <Row label="CFR ref">
            <input value={draft.cfr_ref} onChange={(e) => setDraft({ ...draft, cfr_ref: e.target.value })} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
          </Row>
          <Row label="ISO ref">
            <input value={draft.iso_ref} onChange={(e) => setDraft({ ...draft, iso_ref: e.target.value })} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
          </Row>
          <Row label="Owner">
            <input value={draft.owner} onChange={(e) => setDraft({ ...draft, owner: e.target.value })} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
          </Row>
          <Row label="Status">
            <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900">
              <option value="open">Open</option>
              <option value="verified">Verified</option>
              <option value="validated">Validated</option>
              <option value="closed">Closed</option>
            </select>
          </Row>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-4">
          <button onClick={onClose} className="border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700 hover:border-slate-400">Cancel</button>
          <button data-testid="dc-save" onClick={() => onSave(draft)} disabled={!draft.user_need.trim() && !draft.design_input.trim()} className="inline-flex items-center gap-1.5 bg-[#2563EB] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-40">
            <Save className="h-3.5 w-3.5" /> Save
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="overline mb-1 text-slate-500">{label}</div>
      {children}
    </label>
  );
}
